import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { findPageHelp } from '../../help/pageHelp';
import { pageHelpTarget } from '../../help/pageHelpTarget';
import { findArticle } from '../../help/articles';
import { helpArea } from '../../help/areas';
import '../../pages/help/Help.css';

/**
 * The Help button in the top bar, on every page. It opens the page's help article; a page with
 * several offers them in a short menu; a page with none yet opens the help list for its area, which
 * says so. Reads help/pageHelp.ts, the one list of pages and their help.
 */
export default function PageHelpButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const target = pageHelpTarget(location.pathname, location.search);
  const area = helpArea(findPageHelp(location.pathname)?.area);

  if (target.kind !== 'menu') {
    return (
      <Link to={target.to} className="tt-help-btn" data-tour="page-help">
        <i className="bi bi-question-circle" aria-hidden="true" />
        <span className="tt-help-btn__text">Help</span>
      </Link>
    );
  }

  const go = (to: string) => {
    setAnchor(null);
    navigate(to);
  };
  const from = encodeURIComponent(location.pathname + location.search);
  return (
    <>
      <button
        type="button"
        className="tt-help-btn"
        data-tour="page-help"
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <i className="bi bi-question-circle" aria-hidden="true" />
        <span className="tt-help-btn__text">Help</span>
      </button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {target.slugs.map((slug) => (
          <MenuItem key={slug} onClick={() => go(`/help/${slug}?from=${from}`)}>
            <ListItemText primary={findArticle(slug)!.title} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => go(target.to)}>
          <ListItemText primary={area ? `All help for ${area.label}` : 'All help'} />
        </MenuItem>
      </Menu>
    </>
  );
}
